import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { Payment } from '@vsolv/packages/payments/domain';
import { PaymentService } from '@vsolv/packages/payments/web';
import { Warranty } from '@wsphere/warranties/domain';
import { BehaviorSubject, catchError, combineLatest, from, map, of, shareReplay, startWith, switchMap } from 'rxjs';
import { PaymentItem, PaymentsTableComponent } from '../payments-table/payments-table.component';

interface PaymentDetailsState {
  loading: boolean;
  payment: Payment.Model | null;
  error: string | null;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-warranty-payments',
  templateUrl: './warranty-payments.component.html',
  standalone: false,
})
export class WarrantyPaymentsComponent {
  constructor(private paymentSvc: PaymentService) {}

  private warranty$ = new BehaviorSubject<Warranty.Model | null>(null);
  private selectedPaymentId$ = new BehaviorSubject<string | null>(null);

  @Input() set warranty(warranty: Warranty.Model) {
    if (this.warranty$.value?.id !== warranty.id) this.closePayment();
    this.warranty$.next(warranty);
  }
  get warranty(): Warranty.Model | null {
    return this.warranty$.value;
  }

  @Output() readonly refresh = new EventEmitter<void>();
  @ViewChild(PaymentsTableComponent) paymentsTable?: PaymentsTableComponent;

  readonly filters$ = this.warranty$.pipe(
    map(warranty => (warranty ? { warrantyId: warranty.id, status: Object.values(Payment.Status) } : null))
  );

  readonly paymentDetails$ = combineLatest([this.selectedPaymentId$, this.warranty$]).pipe(
    switchMap(([id, warranty]) => {
      if (!id || !warranty) return of<PaymentDetailsState>({ loading: false, payment: null, error: null });
      return from(this.paymentSvc.retrieve(id)).pipe(
        map(payment => ({
          loading: false,
          payment,
          error: payment ? null : 'This payment is no longer available.',
        })),
        startWith<PaymentDetailsState>({ loading: true, payment: null, error: null }),
        catchError(() =>
          of<PaymentDetailsState>({
            loading: false,
            payment: null,
            error: 'Unable to load payment details. Please try again.',
          })
        )
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  openPayment(payment: PaymentItem) {
    this.selectedPaymentId$.next(payment.id);
  }

  closePayment() {
    this.selectedPaymentId$.next(null);
  }

  retryDetails() {
    this.selectedPaymentId$.next(this.selectedPaymentId$.value);
  }

  paymentUpdated() {
    this.paymentsTable?.refresh();
    this.retryDetails();
    this.refresh.emit();
  }
}
